import { forwardHubbleEventsIntoTealium } from './hubble.js';
import createQueue from './queue.js';
import type { AdapterOptions, TrackingProps, TrackingType } from './types.js';

type LinkFn = (eventName: string | string[], props?: TrackingProps) => void;

type ViewFn = (eventName: string | string[], props?: TrackingProps) => void;

const makeTealiumAdapter = (
  options: AdapterOptions = {},
): { link: LinkFn; view: ViewFn } => {
  const { maxQueueSize = 200 } = options;

  const initialise = () => {
    if (typeof window !== 'undefined') {
      // required for utag to work
      window.utag_data = {};

      // prevents utag from sending a 'view' event on page load
      window.utag_cfg_ovrd = { noview: true };
    }
  };
  const { size, dequeueAll, enqueue } = createQueue<{
    type: TrackingType;
    props: TrackingProps;
  }>({
    max: maxQueueSize,
  });

  const track = (type: TrackingType, props: TrackingProps) => {
    if (typeof window === 'undefined') {
      return;
    }

    if (window.SEEK_TEST === true) {
      return;
    }

    if (window.ANALYTICS_DEBUG) {
      // eslint-disable-next-line no-console
      console.log({ adapter: 'tealium', type, props });
    }

    const { utag } = window;

    if (typeof utag !== 'undefined' && typeof utag.track === 'function') {
      setTimeout(() => {
        utag.track(type, { ...props });
      });
    } else {
      enqueue({ type, props });
      setQueueTimer();
    }
  };

  const setQueueTimer = () => {
    if (size() === 1) {
      const utagReady = setInterval(() => {
        const { utag } = window;

        if (typeof utag !== 'undefined' && typeof utag.track === 'function') {
          clearInterval(utagReady);
          dequeueAll(({ type: eventType, props: eventProps }) =>
            setTimeout(() => {
              utag.track(eventType, eventProps);
            }),
          );
        }
      }, 200);
    }
  };

  const link: LinkFn = (eventName, { linkName, ...props } = {}) =>
    track('link', {
      eventName,
      linkName: linkName ?? eventName,
      ...props,
    });

  const view: ViewFn = (eventName, props = {}) =>
    track('view', { eventName, ...props });

  initialise();

  if (options.hubble) {
    forwardHubbleEventsIntoTealium(options.hubble, track);
  }

  return {
    link,
    view,
  };
};
export default makeTealiumAdapter;
export type TealiumAdapter = ReturnType<typeof makeTealiumAdapter>;
